import dayjs from 'dayjs';
import type {
  GridRowInstance,
  GridRawRowDataType,
} from '@/common/components/molecules/modernGrid/modernGrid.types';
import { compareNumberValues } from '@/common/components/molecules/filterSearch/filterSearch.utils';
import type {
  CustomFilterOption,
  FilterItem,
  FilterSearchOperator,
} from '@/common/components/molecules/filterSearch/filterSearch.type';
import { TrendChartTickInfo, TrendChartFilterField } from './tendChartCell.type';

export const getTrendChartLastValue = (
  data: TrendChartTickInfo | null | undefined,
): number | null => {
  if (!data || typeof data !== 'object' || !Array.isArray(data.values)) {
    return null;
  }

  return data.values[data.values.length - 1] ?? null;
};

/**
 * 최근값으로 인정하는 최대 구간 수(마지막 구간 포함).
 * 틱 간격이 1분이라 2구간이면 최대 2분까지만 수집 지연으로 인정한다.
 * 이 범위를 넘어 비어 있으면 지연이 아니라 데이터 이상으로 본다
 */
const RECENT_VALUE_LOOKBACK_TICKS = 2;

/**
 * 정렬 키로 쓸 최근값. 수집 지연으로 마지막 구간이 null인 경우가 대부분이라
 * 마지막 구간이 비면 직전 구간까지 확인한다.
 * 그보다 더 거슬러 올라가면 장애로 오래 끊긴 행을 옛 값으로 정렬하게 되므로 제한한다
 */
export const getTrendChartLastNumericValue = (
  data: TrendChartTickInfo | null | undefined,
): number | null => {
  const values = data?.values;
  if (!Array.isArray(values)) {
    return null;
  }

  const lastIdx = values.length - 1;
  const oldestIdx = Math.max(0, lastIdx - (RECENT_VALUE_LOOKBACK_TICKS - 1));
  for (let i = lastIdx; i >= oldestIdx; i -= 1) {
    const value = values[i];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return null;
};

export const sortTrendChartCellAsc = (a: TrendChartTickInfo, b: TrendChartTickInfo) => {
  const valueA = getTrendChartLastNumericValue(a);
  const valueB = getTrendChartLastNumericValue(b);

  if (valueA === null && valueB === null) return 0;
  // 최근값 없는 셀은 오름차순 하단. 정렬 방향에 따른 반전은 wrapCustomAscFunction이 담당한다
  if (valueA === null) return 1;
  if (valueB === null) return -1;

  return valueA - valueB;
};

export const sortTrendChartCellAscForModernGrid = <T extends GridRawRowDataType>(
  rowA: GridRowInstance<T>,
  rowB: GridRowInstance<T>,
  columnId: string,
) => {
  const a = rowA.original[columnId];
  const b = rowB.original[columnId];
  return sortTrendChartCellAsc(a, b);
};

/** trend-chart-cell 컬럼은 type: 'object'지만 마지막 값(숫자)으로 매칭하므로 숫자 연산자를 쓴다 */
export const TREND_CHART_FILTER_OPERATORS: FilterSearchOperator[] = [
  '==',
  '!=',
  '>',
  '>=',
  '<',
  '<=',
];

export const applyTrendChartFilterOperators = (
  filterItems: FilterItem[],
  ids: string[],
): FilterItem[] =>
  filterItems.map((item) =>
    ids.includes(String(item.key.id))
      ? { ...item, operators: [...TREND_CHART_FILTER_OPERATORS] }
      : item,
  );

/** 마지막 값 기준 필터 매칭. 옵션 미지정 시 기본 표시(toFixed(2))와 동일하게 비교한다 */
export const matchTrendChartLastValue = (
  cell: TrendChartTickInfo | null | undefined,
  token: { operator: string; value: string },
  { formatValue, parseSearchValue }: Omit<TrendChartFilterField, 'id'> = {},
): boolean => {
  const last = getTrendChartLastValue(cell);
  if (last == null) {
    return false;
  }

  const searchValue = parseSearchValue ? parseSearchValue(token.value) : token.value;
  if (searchValue === undefined) {
    return false;
  }

  if (formatValue && (token.operator === '==' || token.operator === '!=')) {
    const isMatch = formatValue(last) === formatValue(Number(searchValue));
    return token.operator === '==' ? isMatch : !isMatch;
  }

  return compareNumberValues({
    cellValue: formatValue ? last : Number(last.toFixed(2)),
    searchValue,
    operator: token.operator,
  });
};

export const createTrendChartFilterOptions = (
  fields: (string | TrendChartFilterField)[],
): CustomFilterOption[] =>
  fields.map((field) => {
    const { id, ...option } = typeof field === 'string' ? { id: field } : field;
    return {
      id,
      filterFn: (row, token) =>
        matchTrendChartLastValue((row as Record<string, TrendChartTickInfo>)[id], token, option),
    };
  });

interface TrendChartTooltipOptions {
  timeFormat?: string;
  /** 하단 문구. 미지정 시 미출력 */
  footer?: string;
  /** 값 포맷터(단위 변환 + 단위 표기 등). 미지정 시 입력값을 그대로 표시 */
  formatValue?: (y: number) => string;
  /** 본문에 표시할 시리즈 라벨(단위 미포함). 미지정 시 라벨 미출력 */
  label?: string;
}

const formatTooltipValue = (
  y: number | string | undefined,
  formatValue?: (value: number) => string,
): string => {
  if (y == null) {
    return '-';
  }
  const formatted = formatValue ? formatValue(+y) : `${y}`;
  return formatted || '-';
};

export const buildTrendChartTooltip = (
  seriesList: {
    color?: string;
    name?: string;
    data?: { x?: number | string; y?: number | string };
  }[],
  {
    timeFormat = 'HH:mm:ss',
    footer,
    formatValue,
    label: labelText = '',
  }: TrendChartTooltipOptions = {},
): string => {
  const series = seriesList?.[0];
  const point = series?.data;
  const time = point?.x != null ? dayjs(point.x).format(timeFormat) : '';
  const value = formatTooltipValue(point?.y, formatValue);

  const dot = series?.color
    ? `<span class="trend-chart-cell-tooltip__dot" style="background-color:${series.color}"></span>`
    : '';
  const label = labelText
    ? `<span class="trend-chart-cell-tooltip__label">${dot}${labelText}</span>`
    : '';
  const footerHtml = footer ? `<div class="trend-chart-cell-tooltip__footer">${footer}</div>` : '';

  return `<div class="ev-chart-tooltip-custom trend-chart-cell-tooltip"><div class="trend-chart-cell-tooltip__header">${time}</div><div class="trend-chart-cell-tooltip__body">${label}<span class="trend-chart-cell-tooltip__value">${value}</span></div>${footerHtml}</div>`;
};

export const trendChartDataExcelExportFormatter = (
  cellData?: TrendChartTickInfo,
): string | number | null => {
  if (!cellData) return null;
  const { values } = cellData;
  if (!Array.isArray(values) || values.length === 0) {
    return null;
  }
  const lastMetric = values[values.length - 1];

  if (typeof lastMetric === 'number' || typeof lastMetric === 'string') {
    return lastMetric;
  }
  return null;
};
